import { type Response, sendGet, sendPost, sendPostFormData } from '../hooks/use-fetch'
import type { AnnualPlanStatus } from '../models/AnnualPlanStatus'
import type { AnnualPlanTeamMember } from '../models/AnnualPlanTeamMember'
import type { PlanOrganizationTask } from '../models/PlanOrganizationTask'
import { type RCNPD } from '../models/RCNPD'
import { type UpdateTrainingFormValues, type AddTrainingFormValues } from '../pages/pageComponents/planning/priorityActivities/PriorityOne/AddEditTrainingModal'
import type { AddAnnualPlanRequest, AnnualPlanTeamSaveRequest, RevisionRequestRequest } from '../models/requestObjects/AnnualPlanRequestObjects'
import {
  type AnnualPlanNameAndOrgName,
  type AnnualPlanOption,
  type AnnualPlanTeamAndOptions,
  type DistrictAndBuildingsOptions
} from '../models/requestObjects/AnnualPlanResponseObjects'
import type { OrganizationOption } from '../models/requestObjects/CommonObjects'
import { type CETAClassroomAndFocusStudents } from '../models/CETAClassroomAndFocusStudents'
import {
  type AddCetaClassroomAndFocusStudentsFormValues,
  type UpdateCetaClassroomAndFocusStudentsFormValues
} from '../pages/pageComponents/planning/priorityActivities/PriorityThree/CetaClassroomsandFocusStudentsModal'
import { type PlanOrganization } from '../models/PlanOrganization'
import { type StartTrainingAttendancePlan } from '../models/StartTrainingAttendancePlan'
import {
  type AddStartTrainingAttendancePlanFormValues, type UpdateStartTrainingAttendancePlanFormValues
} from '../pages/pageComponents/planning/priorityActivities/PriorityOne/StartTrainingAttendancePlanModal'
import { type StartLeadershipDayAndConference } from '../models/StartLeadershipDayAndConference'
import {
  type AddStartLeadershipDayAndConferenceFormValues,
  type UpdateStartLeadershipDayAndConferenceFormValues
} from '../pages/pageComponents/planning/priorityActivities/PriorityOne/StartLeadershipDayAndConferenceModal'
import { type StartCommunityOfPractice } from '../models/StartCommunityOfPractice'
import type { AddStartCommunityOfPracticeFormValues, UpdateStartCommunityOfPracticeFormValues } from '../pages/pageComponents/planning/priorityActivities/PriorityOne/StartCommunityOfPracticeModal'
import { type Priority } from '../models/Priority'
import { type UserPerson } from '../models/UserPerson'
import { type BudgetSummary } from '../models/BudgetSummary'
import { type PlanRevisionRequest } from '../models/PlanRevisionRequest'
import { type CoachingPlan } from '../models/CoachingPlan'
import { type SelectableOption } from '../utils/genericTypes'
import type { OrganizationDetailsModel } from '../models/OrganizationDetailsModel'
import { type FocusBuilding } from '../models/FocusBuilding'
import { type IsdDistrictProgramEnhancement } from '../models/IsdDistrictProgramEnhancement'
import {
  type AddIsdDistrictProgramEnhancementFormValues, type UpdateIsdDistrictProgramEnhancementFormValues
} from '../pages/pageComponents/planning/priorityActivities/priorityFour/IsdDistrictProgramEnhancementModal'
import { type PlanContract } from '../models/PlanContract'
import { type TrainingType, type Training } from '../models/Training'
import { type UpdateFocusBuildingRequest, type AddFocusBuildingRequest, type TrackFocusBuildingRequest } from '../models/requestObjects/priorityFour/focusBuilding/FocusBuildingRequestObjects'
import { type PlanOrganizationOrganizations } from '../models/PlanOrganizationOrganizations'
import { type PriorityOneReviewType } from '../models/PriorityOneReviewType'

export async function GetAnnualPlanTeam (annualPlanId: number): Promise<AnnualPlanTeamMember[]> {
  const { response } = await sendGet(`AnnualPlan/GetAnnualPlanTeam?annualPlanId=${annualPlanId}`)
  return response
}

export async function GetAnnualPlanTeamByPlanOrganizationTaskId (planOrganizationTaskId: number): Promise<Response<UserPerson[]>> {
  return await sendGet(`AnnualPlan/GetAnnualPlanTeamByPlanOrganizationTaskId?planOrganizationTaskId=${planOrganizationTaskId}`)
}

export async function GetAnnualPlanTeamAndOptions (annualPlanId: number): Promise<AnnualPlanTeamAndOptions> {
  const { response } = await sendGet(`AnnualPlan/GetAnnualPlanTeamAndOptions?annualPlanId=${annualPlanId}`)
  return response
}

export async function SaveAnnualPlanTeam (teamRequest: AnnualPlanTeamSaveRequest): Promise<void> {
  const { response } = await sendPost('AnnualPlan/SaveAnnualPlanTeam', teamRequest)
  return response
}

export async function SendPlanOrgTaskReminder (taskId: number): Promise<void> {
  const { response } = await sendPost('AnnualPlan/SendTaskReminder', { taskId })
  return response
}

export async function GetTasksForAnnualPlan (annualPlanId: number): Promise<PlanOrganizationTask[]> {
  const { response } = await sendGet(`AnnualPlan/GetTasksForAnnualPlan?annualPlanId=${annualPlanId}`)
  return response
}

export async function GetDashboardTasks (annualPlanId: number, implementationPhase: boolean): Promise<PlanOrganizationTask[]> {
  const { response } = await sendGet(`AnnualPlan/GetDashboardTasks?annualPlanId=${annualPlanId}&&implementationPhase=${String(implementationPhase)}`)
  return response
}

export async function GetUsersPlanRole (annualPlanId: number): Promise<string> {
  const request = await sendGet(`AnnualPlan/GetUsersPlanRole?annualPlanId=${annualPlanId}`)
  const role = request.response
  return role
}

export async function GetAnnualPlanAndOrgName (annualPlanId: string, organizationId: string): Promise<AnnualPlanNameAndOrgName> {
  const { response } = await sendGet(`AnnualPlan/GetAnnualPlanAndOrgName?annualPlanId=${annualPlanId}&&orgId=${organizationId}`)
  return response
}

export async function GetAnnualPlanOptions (rcnId: number): Promise<AnnualPlanOption[]> {
  const { response } = await sendGet(`AnnualPlan/GetAnnualPlanOptions?rcnId=${rcnId}`)
  return response
}

export async function AddAnnualPlan (request: AddAnnualPlanRequest): Promise<Response> {
  return await sendPost('AnnualPlan/AddAnnualPlan', request)
}

export async function GetPlanOrganizationsForAnnualPlan (annualPlanId: number, getRcn: boolean = false): Promise<Response<OrganizationOption[]>> {
  return await sendGet(`AnnualPlan/GetPlanOrganizationsForAnnualPlan?annualPlanId=${annualPlanId}&getRcn=${String(getRcn)}`)
}

export async function GetPlanOrganization (planOrgId: number): Promise<Response<PlanOrganization>> {
  return await sendGet(`AnnualPlan/GetPlanOrganization?planOrganizationId=${planOrgId}`)
}

export async function GetAnnualPlanStatus (annualPlanId: number): Promise<AnnualPlanStatus | undefined> {
  const { response } = await sendGet(`AnnualPlan/GetAnnualPlanStatus?annualPlanId=${annualPlanId}`)
  return response
}

export async function StartAnnualPlan (annualPlanId: number, rcnId: number): Promise<void> {
  const { response } = await sendPost('AnnualPlan/StartAnnualPlan', { annualPlanId, rcnId })
  return response
}

export async function UpdatePlanOrganizationTaskStatus (planOrganizationTaskId: number, status: string, isImplementation?: boolean): Promise<Response> {
  return await sendPost('AnnualPlan/UpdatePlanOrganizationTaskStatus', { planOrganizationTaskId, status, isImplementation })
}

export async function AddRCNPD (request: AddTrainingFormValues): Promise<Response> {
  return await sendPost('AnnualPlan/AddRCNPD', request)
}

export async function UpdateRCNPD (request: UpdateTrainingFormValues): Promise<Response> {
  return await sendPost('AnnualPlan/UpdateRCNPD', request)
}

export async function DeleteRCNPD (rcnPDId: number, isUpdate?: boolean, implementationPhase?: boolean): Promise<Response> {
  return await sendPost(`AnnualPlan/DeleteRCNPD?rcnPDId=${rcnPDId}&&isUpdate=${String(isUpdate ?? false)}&&implementationPhase=${String(implementationPhase ?? false)}`, {})
}

export async function GetPlanOrganizationOrganizations (planOrganizationId: number): Promise<Response<PlanOrganizationOrganizations>> {
  return await sendGet(`AnnualPlan/GetPlanOrganizationOrganizations?planOrganizationId=${planOrganizationId}`)
}

export async function GetIsdsAndDistrictsForPlanOrganization (planOrganizationId: number): Promise<OrganizationOption[]> {
  const { response } = await sendGet(`AnnualPlan/GetIsdsAndDistrictsForPlanOrganization?planOrgId=${planOrganizationId}`)
  return response
}

export async function GetRCNPDsForPriority (priorityId: number, contractOnly: boolean = true): Promise<RCNPD[]> {
  const { response } = await sendGet(`AnnualPlan/GetRCNPDsForPriority?priorityId=${priorityId}&&contractOnly=${String(contractOnly)}`)
  return response
}

export async function GetTrainings (trainingType: TrainingType): Promise<Training[]> {
  const { response } = await sendGet(`AnnualPlan/GetTrainings?trainingType=${trainingType}`)
  return response
}

export async function GetPlanOrganizationTaskId (annualPlanId: number, orgId: number, taskName: string): Promise<number> {
  const { response } = await sendGet(`AnnualPlan/GetPlanOrganizationTaskId?annualPlanId=${annualPlanId}&&orgId=${orgId}&&taskName=${taskName.replaceAll(' ', '%20')}`)
  return response
}

export async function GetPlanOrganizationTask (annualPlanId: number, orgId: number, taskName: string): Promise<Response<PlanOrganizationTask>> {
  return await sendGet(`AnnualPlan/GetPlanOrganizationTask?annualPlanId=${annualPlanId}&&orgId=${orgId}&&taskName=${taskName.replaceAll(' ', '%20')}`)
}

export async function GetPlanOrganizationTaskSubset (annualPlanId: number, orgId: number, taskNames: string[]): Promise<Response<PlanOrganizationTask[]>> {
  return await sendPost(`AnnualPlan/GetPlanOrganizationTaskSubset?annualPlanId=${annualPlanId}&&orgId=${orgId}`, taskNames)
}

export async function GetPlanOrganizationTaskById (planOrganizationTaskId: number): Promise<Response<PlanOrganizationTask>> {
  return await sendGet(`AnnualPlan/GetPlanOrganizationTaskById?planOrganizationTaskId=${planOrganizationTaskId}`)
}

export async function GetPriority (planOrganizationTaskId: number): Promise<Response<Priority>> {
  return await sendGet(`AnnualPlan/GetPriority?planOrganizationTaskId=${planOrganizationTaskId}`)
}

export async function GetCetaClassroomsAndFocusStudents (priorityId: number, contractOnly: boolean = true): Promise<CETAClassroomAndFocusStudents[]> {
  const { response } = await sendGet(`AnnualPlan/GetCetaClassroomsAndFocusStudents?priorityId=${priorityId}&&contractOnly=${String(contractOnly)}`)
  return response
}

export async function AddCetaClassroomAndFocusStudents (cetaClassroomsAndFocusStudents: AddCetaClassroomAndFocusStudentsFormValues): Promise<Response> {
  return await sendPost('AnnualPlan/AddCetaClassroomAndFocusStudents', cetaClassroomsAndFocusStudents)
}

export async function UpdateCetaClassroomAndFocusStudents (cetaClassroomsAndFocusStudents: UpdateCetaClassroomAndFocusStudentsFormValues): Promise<Response> {
  return await sendPost('AnnualPlan/UpdateCetaClassroomAndFocusStudents', cetaClassroomsAndFocusStudents)
}

export async function DeleteCetaClassroomAndFocusStudents (cetaClassroomAndFocusStudentsId: number, isUpdate: boolean = false): Promise<Response> {
  return await sendPost(`AnnualPlan/DeleteCetaClassroomAndFocusStudents?cetaClassroomAndFocusStudentsId=${cetaClassroomAndFocusStudentsId}&&isUpdate=${String(isUpdate)}`, {})
}

export async function GetDistrictsAndBuildingsForPlanOrganization (planOrgId: number): Promise<DistrictAndBuildingsOptions[]> {
  const { response } = await sendGet(`AnnualPlan/GetDistrictsAndBuildingsForPlanOrganization?planOrgId=${planOrgId}`)
  return response
}

export async function GetEEMIdByOrgId (orgId: number): Promise<number | null> {
  const { response } = await sendGet(`AnnualPlan/GetEEMIdByOrgId?orgId=${orgId}`)
  return response
}

export async function GetEEMDistrictsAndBuildingsForPlanOrganization (planOrgId: number): Promise<DistrictAndBuildingsOptions[]> {
  const { response } = await sendGet(`AnnualPlan/GetEEMDistrictsAndBuildingsForPlanOrganization?planOrgId=${planOrgId}`)
  return response
}

export async function GetEEMBuildingsForOrganization (organizationId: number): Promise<OrganizationOption[]> {
  const { response } = await sendGet(`AnnualPlan/GetEEMBuildingsForOrganization?organizationId=${organizationId}`)
  return response
}

export async function GetStartTrainingAttendancePlans (priorityId: number, includeUpdates: boolean): Promise<StartTrainingAttendancePlan[]> {
  const { response } = await sendGet(`AnnualPlan/GetStartTrainingAttendancePlans?priorityId=${priorityId}&includeUpdates=${String(includeUpdates)}`)
  return response
}

export async function AddStartTrainingAttendancePlan (startTrainingAttendancePlan: AddStartTrainingAttendancePlanFormValues): Promise<Response> {
  return await sendPost('AnnualPlan/AddStartTrainingAttendancePlan', startTrainingAttendancePlan)
}

export async function UpdateStartTrainingAttendancePlan (startTrainingAttendancePlan: UpdateStartTrainingAttendancePlanFormValues): Promise<Response> {
  return await sendPost('AnnualPlan/UpdateStartTrainingAttendancePlan', { ...startTrainingAttendancePlan, expandedContent: undefined })
}

export async function TrackStartTrainingAttendancePlan (startEventAttendancePlan: StartTrainingAttendancePlan): Promise<Response> {
  return await sendPost('AnnualPlan/TrackStartTrainingAttendancePlan', startEventAttendancePlan)
}

export async function DeleteStartTrainingAttendancePlanTracking (startTrainingAttendancePlanId: number): Promise<Response> {
  return await sendPost(`AnnualPlan/DeleteStartTrainingAttendancePlanTracking?startTrainingAttendancePlanId=${startTrainingAttendancePlanId}`, {})
}

export async function DeleteStartTrainingAttendancePlan (startTrainingAttendancePlanId: number, isUpdate: boolean): Promise<Response> {
  return await sendPost(`AnnualPlan/DeleteStartTrainingAttendancePlan?startTrainingAttendancePlanId=${startTrainingAttendancePlanId}&isUpdate=${String(isUpdate)}`, {})
}

export async function GetStartLeadershipDayAndConferences (priorityId: number, includeUpdates: boolean): Promise<StartLeadershipDayAndConference[]> {
  const { response } = await sendGet(`AnnualPlan/GetStartLeadershipDayAndConferences?priorityId=${priorityId}&includeUpdates=${String(includeUpdates)}`)
  return response
}

export async function AddStartLeadershipDayAndConference (startLeadershipDayAndConferences: Record<string, AddStartLeadershipDayAndConferenceFormValues>): Promise<Response> {
  return await sendPost('AnnualPlan/AddStartLeadershipDayAndConference', startLeadershipDayAndConferences)
}

export async function UpdateStartLeadershipDayAndConference (startLeadershipDayAndConferences: Record<string, UpdateStartLeadershipDayAndConferenceFormValues>): Promise<Response> {
  return await sendPost('AnnualPlan/UpdateStartLeadershipDayAndConference', startLeadershipDayAndConferences)
}

export async function TrackStartLeadershipDayAndConference (startLeadershipDayAndConference: StartLeadershipDayAndConference): Promise<Response> {
  return await sendPost('AnnualPlan/TrackStartLeadershipDayAndConference', startLeadershipDayAndConference)
}

export async function DeleteStartLeadershipDayAndConferenceTracking (startLeadershipDayAndConferenceId: number): Promise<Response> {
  return await sendPost(`AnnualPlan/DeleteStartLeadershipDayAndConferenceTracking?startLeadershipDayAndConferenceId=${startLeadershipDayAndConferenceId}`, {})
}

export async function DeleteStartLeadershipDayAndConference (startLeadershipDayAndConferenceId: number, isUpdate: boolean): Promise<Response> {
  return await sendPost(`AnnualPlan/DeleteStartLeadershipDayAndConference?startLeadershipDayAndConferenceId=${startLeadershipDayAndConferenceId}&isUpdate=${String(isUpdate)}`, {})
}

export async function GetStartCommunityOfPractices (priorityId: number, includeUpdates: boolean): Promise<StartCommunityOfPractice[]> {
  const { response } = await sendGet(`AnnualPlan/GetStartCommunityOfPractices?priorityId=${priorityId}&includeUpdates=${String(includeUpdates)}`)
  return response
}

export async function GetStartCommunityOfPractice (startCommunityOfPracticeId: number): Promise<StartCommunityOfPractice> {
  const { response } = await sendGet(`AnnualPlan/GetStartCommunityOfPractice?startCommunityOfPracticeId=${startCommunityOfPracticeId}`)
  return response
}

export async function AddStartCommunityOfPractice (startCommunityOfPractices: Record<string, AddStartCommunityOfPracticeFormValues>): Promise<Response> {
  return await sendPost('AnnualPlan/AddStartCommunityOfPractice', startCommunityOfPractices)
}

export async function UpdateStartCommunityOfPractice (startCommunityOfPractices: Record<string, UpdateStartCommunityOfPracticeFormValues>): Promise<Response> {
  return await sendPost('AnnualPlan/UpdateStartCommunityOfPractice', startCommunityOfPractices)
}

export async function DeleteStartCommunityOfPractice (startCommunityOfPracticeId: number, isUpdate: boolean): Promise<Response> {
  return await sendPost(`AnnualPlan/DeleteStartCommunityOfPractice?startCommunityOfPracticeId=${startCommunityOfPracticeId}&isUpdate=${String(isUpdate)}`, {})
}

export async function TrackStartCommunityOfPractice (startCommunityOfPractice: StartCommunityOfPractice): Promise<Response> {
  return await sendPost('AnnualPlan/TrackStartCommunityOfPractice', startCommunityOfPractice)
}

export async function DeleteStartCommunityOfPracticeTracking (startCommunityOfPracticeId: number): Promise<Response> {
  return await sendPost(`AnnualPlan/DeleteStartCommunityOfPracticeTracking?startCommunityOfPracticeId=${startCommunityOfPracticeId}`, {})
}

export async function GetBudgetSummary (annualPlanId: number): Promise<BudgetSummary> {
  const { response } = await sendGet(`AnnualPlan/GetBudgetSummary?annualPlanId=${annualPlanId}`)
  return response
}

export async function GetRevisionRequests (annualPlanId: number): Promise<PlanRevisionRequest[]> {
  const { response } = await sendGet(`AnnualPlan/GetRevisionRequests?annualPlanId=${annualPlanId}`)
  return response
}

export async function AddRevisionRequests (annualPlanId: number, requests: RevisionRequestRequest[]): Promise<void> {
  await sendPost(`AnnualPlan/AddRevisionRequests?annualPlanId=${annualPlanId}`, requests)
}

export async function ResolveRevisionRequest (revisionRequestId: number): Promise<Response> {
  return await sendPost(`AnnualPlan/ResolveRevisionRequest?revisionRequestId=${revisionRequestId}`, {})
}

export async function SubmitPlan (annualPlanId: number, rcnId: number, implementationPhase: boolean = false): Promise<Response> {
  return await sendPost(`AnnualPlan/SubmitPlan?annualPlanId=${annualPlanId}&rcnId=${rcnId}&implementationPhase=${String(implementationPhase)}`, {})
}

export async function DeleteCoachingPlan (coachingPlanId: number, isUpdate: boolean = false, implementationPhase: boolean = false): Promise<Response> {
  return await sendPost(`AnnualPlan/DeleteCoachingPlan?coachingPlanId=${coachingPlanId}&isUpdate=${String(isUpdate)}&implementationPhase=${String(implementationPhase)}`, {})
}

export async function AddCoachingPlan (coachingPlan: CoachingPlan): Promise<Response> {
  return await sendPost('AnnualPlan/AddCoachingPlan', coachingPlan)
}

export async function UpdateCoachingPlan (coachingPlan: CoachingPlan): Promise<Response> {
  return await sendPost('AnnualPlan/UpdateCoachingPlan', coachingPlan)
}

export async function GetCoachingPlans (priorityId: number, contractOnly: boolean = true): Promise<CoachingPlan[]> {
  const { response } = await sendGet(`AnnualPlan/GetCoachingPlans?priorityId=${priorityId}&&contractOnly=${String(contractOnly)}`)
  return response
}

export async function GetDistrictsFromISDPlanOrgId (planOrgId: number): Promise<SelectableOption[]> {
  const { response } = await sendGet(`AnnualPlan/GetDistrictsFromISDPlanOrgId?planOrgId=${planOrgId}`)
  return response
}

export async function GetOrganizationFromPlanOrgId (planOrgId: number): Promise<OrganizationDetailsModel> {
  const { response } = await sendGet(`AnnualPlan/GetOrganizationFromPlanOrgId?planOrgId=${planOrgId}`)
  return response
}

export async function AcknowledgeCoachingTool (priorityId: number, acknowledged: boolean): Promise<OrganizationDetailsModel> {
  const { response } = await sendPost(`AnnualPlan/AcknowledgeCoachingTool?priorityId=${priorityId}&acknowledged=${String(acknowledged)}`, {})
  return response
}

export async function GetFocusBuildings (priorityId: number, contractOnly: boolean = false): Promise<Response<FocusBuilding[]>> {
  return await sendGet(`AnnualPlan/GetFocusBuildings?priorityId=${priorityId}&&contractOnly=${String(contractOnly)}`)
}

export async function AddFocusBuilding (focusBuilding: AddFocusBuildingRequest): Promise<Response> {
  return await sendPost('AnnualPlan/AddFocusBuilding', focusBuilding)
}

export async function UpdateFocusBuilding (focusBuilding: UpdateFocusBuildingRequest): Promise<Response> {
  return await sendPost('AnnualPlan/UpdateFocusBuilding', focusBuilding)
}

export async function TrackFocusBuilding (trackRequest: TrackFocusBuildingRequest): Promise<Response> {
  return await sendPost('AnnualPlan/TrackFocusBuilding', trackRequest)
}

export async function ClearFocusBuildingTracking (trackRequest: { id: number }): Promise<Response> {
  return await sendPost('AnnualPlan/ClearFocusBuildingTracking', trackRequest)
}

export async function DeleteFocusBuilding (focusBuildingId: number, isUpdate: boolean = false): Promise<Response> {
  return await sendPost(`AnnualPlan/DeleteFocusBuilding?focusBuildingId=${focusBuildingId}&isUpdate=${String(isUpdate)}`, {})
}

export async function GetIsdDistrictProgramEnhancements (priorityId: number, contractOnly: boolean = true): Promise<Response<IsdDistrictProgramEnhancement[]>> {
  return await sendGet(`AnnualPlan/GetIsdDistrictProgramEnhancements?priorityId=${priorityId}&&contractOnly=${String(contractOnly)}`)
}

export async function AddIsdDistrictProgramEnhancement (isdDistrictProgramEnhancement: AddIsdDistrictProgramEnhancementFormValues): Promise<Response> {
  return await sendPost('AnnualPlan/AddIsdDistrictProgramEnhancement', isdDistrictProgramEnhancement)
}

export async function UpdateIsdDistrictProgramEnhancement (isdDistrictProgramEnhancement: UpdateIsdDistrictProgramEnhancementFormValues): Promise<Response> {
  return await sendPost('AnnualPlan/UpdateIsdDistrictProgramEnhancement', isdDistrictProgramEnhancement)
}

export async function DeleteIsdDistrictProgramEnhancement (isdDistrictProgramEnhancementId: number, isUpdate: boolean = false, implementationPhase: boolean = false): Promise<Response> {
  return await sendPost(`AnnualPlan/DeleteIsdDistrictProgramEnhancement?isdDistrictProgramEnhancementId=${isdDistrictProgramEnhancementId}&&isUpdate=${String(isUpdate)}&&implementationPhase=${String(implementationPhase)}`, {})
}

export async function LockActivity (activityId: number, activityName: string, isUpdate = false): Promise<Response> {
  return await sendPost('AnnualPlan/LockActivity', { activityId, activityName, isUpdate })
}

export async function UnlockActivity (activityId: number, activityName: string, isUpdate = false): Promise<Response> {
  return await sendPost('AnnualPlan/UnlockActivity', { activityId, activityName, isUpdate })
}

export async function UploadStartContract (planOrganizationTaskId: number, file: File): Promise<Response> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('planOrganizationTaskId', planOrganizationTaskId.toString())
  return await sendPostFormData('AnnualPlan/UploadStartContract', formData)
}

export async function DeleteStartContract (planOrganizationTaskId: number): Promise<Response> {
  return await sendPost(`AnnualPlan/DeleteStartContract?planOrganizationTaskId=${planOrganizationTaskId}`, {})
}

export async function UploadRCNContract (planOrganizationTaskId: number, file: File): Promise<Response> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('planOrganizationTaskId', planOrganizationTaskId.toString())
  return await sendPostFormData('AnnualPlan/UploadRCNContract', formData)
}

export async function DeleteRCNContract (planOrganizationTaskId: number): Promise<Response> {
  return await sendPost(`AnnualPlan/DeleteRCNContract?planOrganizationTaskId=${planOrganizationTaskId}`, {})
}

export async function GetContract (annualPlanId: number): Promise<PlanContract> {
  const { response } = await sendGet(`AnnualPlan/GetContract?annualPlanId=${annualPlanId}`)
  return response
}

export async function SubmitContract (planOrganizationTaskId: number): Promise<Response> {
  return await sendPost(`AnnualPlan/SubmitContract?planOrganizationTaskId=${planOrganizationTaskId}`, {})
}

export async function AcceptContract (planOrganizationTaskId: number): Promise<Response> {
  return await sendPost(`AnnualPlan/AcceptContract?planOrganizationTaskId=${planOrganizationTaskId}`, {})
}

export async function CanModifyPlanOrganization (planOrganizationId: number, contractPhase: boolean): Promise<Response<boolean>> {
  return await sendGet(`AnnualPlan/CanModifyPlanOrganization?planOrganizationId=${planOrganizationId}&contractPhase=${contractPhase.toString()}`)
}

export async function StartImplementationPlan (annualPlanId: number, rcnId: number): Promise<Response<boolean>> {
  return await sendPost('AnnualPlan/StartImplementationPlan', { annualPlanId, rcnId })
}

export async function UpdatePriorityOneReview (type: PriorityOneReviewType, priorityOneId: number, isCompleted: boolean): Promise<Response> {
  const queryString = `?type=${type}&priorityOneId=${priorityOneId}&isCompleted=${String(isCompleted)}`
  return await sendPost(`AnnualPlan/UpdatePriorityOneReview${queryString}`, {})
}
